/**
 * Promo Bar Script - Actualiza la barra de promoción dinámicamente con AJAX
 */

class PromoBar {
  constructor() {
    this.promoBar = document.querySelector('[data-promo-bar]');
    this.updateInterval = null;
    this.lastTotal = null;
    this.initializeObservers();
  }

  initializeObservers() {
    if (!this.promoBar) {
      return;
    }

    // Observar el elemento hdt-cart-drawer para cambios en atributos
    const cartDrawer = document.querySelector('hdt-cart-drawer');
    
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        this.fetchAndUpdate();
      });
      
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['data-total-price', 'data-count'],
        attributeOldValue: true,
        subtree: false
      });
    }

    // Observar cambios en el contenido del carrito (items añadidos/removidos)
    const cartItemsComponent = document.querySelector('cart-items-component');
    
    if (cartItemsComponent) {
      const observer = new MutationObserver((mutations) => {
        setTimeout(() => this.fetchAndUpdate(), 100);
      });
      
      observer.observe(cartItemsComponent, {
        childList: true,
        subtree: true
      });
    }

    // Escuchar eventos de Shopify
    document.addEventListener('cart:updated', () => {
      this.fetchAndUpdate();
    });
    
    document.addEventListener('cartUpdated', () => {
      this.fetchAndUpdate();
    });

    // Escuchar clicks en botones de agregar/eliminar
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-remove]') || 
          e.target.closest('[data-quantity-change]') ||
          e.target.closest('.cart-item__quantity-button') ||
          e.target.closest('[aria-label*="Remove"]')) {
        setTimeout(() => this.fetchAndUpdate(), 200);
      }
    });

    // Detectar si el drawer está abierto y hacer polling
    this.setupDrawerDetection();

    // Actualizar al cargar la página
    setTimeout(() => {
      this.fetchAndUpdate();
    }, 500);
  }

  setupDrawerDetection() {
    const drawer = document.querySelector('#CartDrawer');
    
    if (!drawer) return;

    // Revisar cada 500ms si el drawer está abierto
    setInterval(() => {
      const isOpen = drawer.hasAttribute('open') || drawer.open;
      
      if (isOpen && !this.updateInterval) {
        this.startPeriodicUpdate();
      } else if (!isOpen && this.updateInterval) {
        this.stopPeriodicUpdate();
      }
    }, 500);
  }

  startPeriodicUpdate() {
    if (this.updateInterval) return;
    this.updateInterval = setInterval(() => {
      this.fetchAndUpdate();
    }, 500);
  }

  stopPeriodicUpdate() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  fetchAndUpdate() {
    this.fetchCartViaAjax();
  }

  fetchCartViaAjax() {
    fetch('/cart.json')
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        if (data && data.total_price !== undefined) {
          const currentTotal = data.total_price / 100;
          
          if (this.lastTotal !== currentTotal) {
            this.lastTotal = currentTotal;
            this.updatePromoBar(currentTotal);
          }
        }
      })
      .catch(error => {
        // Silenciar errores en producción
      });
  }

  updatePromoBar(currentTotal) {
    if (!this.promoBar) return;

    const discountLevels = this.getDiscountLevels();
    
    if (discountLevels.length === 0) return;

    discountLevels.sort((a, b) => a.amount - b.amount);

    let currentLevel = null;
    let nextLevel = null;

    for (let level of discountLevels) {
      if (currentTotal >= level.amount) {
        currentLevel = level;
      } else if (!nextLevel) {
        nextLevel = level;
      }
    }

    this.renderPromoBar(currentTotal, currentLevel, nextLevel);
  }

  getDiscountLevels() {
    const levels = [];
    
    if (!this.promoBar) return levels;

    for (let i = 1; i <= 5; i++) {
      const amount = this.promoBar.getAttribute(`data-level-${i}-amount`);
      const discount = this.promoBar.getAttribute(`data-level-${i}-discount`);
      const enabled = this.promoBar.getAttribute(`data-level-${i}-enabled`);

      if (enabled === 'true' && amount && discount) {
        levels.push({
          amount: parseInt(amount) || 0,
          discount: parseInt(discount) || 0
        });
      }
    }

    return levels;
  }

  parsePrice(priceString) {
    const cleaned = priceString.replace(/[^\d.]/g, '');
    return parseFloat(cleaned) || 0;
  }

  renderPromoBar(currentTotal, currentLevel, nextLevel) {
    const container = this.promoBar.querySelector('.hdt-pdp-promo-container');
    
    if (!nextLevel && !currentLevel) {
      this.promoBar.style.display = 'none';
      return;
    }

    this.promoBar.style.display = 'block';

    if (!container) return;

    // Obtener todos los niveles para cálculos correctos
    const allLevels = this.getDiscountLevels();
    const maxAmount = Math.max(...allLevels.map(l => l.amount));

    // Actualizar los círculos (ticks)
    this.updateTicks(currentTotal * 100); // Multiplicar por 100 para centavos

    // Calcular progreso como porcentaje respecto al máximo nivel
    let progressPercent = (currentTotal / maxAmount) * 100;
    
    if (progressPercent > 100) progressPercent = 100;
    if (progressPercent < 0) progressPercent = 0;

    const progressFill = this.promoBar.querySelector('.hdt-pdp-promo-fill');
    if (progressFill) {
      progressFill.style.width = progressPercent + '%';
    }

    const message = this.promoBar.querySelector('.hdt-pdp-promo-text');
    if (message) {
      if (currentLevel && !nextLevel) {
        const texto = `¡Excelente! Has alcanzado el máximo descuento`;
        message.innerHTML = texto;
      } else if (nextLevel) {
        const remaining = nextLevel.amount - currentTotal;
        const formattedRemaining = this.formatPrice(remaining);
        const texto = `Te faltan <span class="hdt-highlight">${formattedRemaining}</span> para obtener <span class="hdt-highlight">${nextLevel.discount}% de descuento</span>`;
        message.innerHTML = texto;
      }
    }
  }

  updateTicks(cartTotalCents) {
    const ticks = this.promoBar.querySelectorAll('.hdt-pdp-promo-tick');
    const allLevels = this.getDiscountLevels();
    
    ticks.forEach((tick, index) => {
      if (allLevels[index]) {
        const levelAmountCents = allLevels[index].amount * 100;
        if (cartTotalCents >= levelAmountCents) {
          tick.classList.add('is-active');
        } else {
          tick.classList.remove('is-active');
        }
      }
    });
  }

  renderMilestones() {
    const allLevels = this.getDiscountLevels();
    if (allLevels.length === 0) return;

    const milestonesContainer = this.promoBar.querySelector('[data-milestones]');
    if (!milestonesContainer) return;

    // Limpiar marcadores anteriores
    milestonesContainer.innerHTML = '';

    // Obtener el color personalizado de los marcadores
    const milestoneColor = this.promoBar.getAttribute('data-milestone-color') || '#6b7280';

    // Obtener el máximo nivel para calcular porcentajes
    const maxAmount = Math.max(...allLevels.map(l => l.amount));

    // Crear marcador para cada nivel
    allLevels.forEach(level => {
  const percentPosition = (level.amount / maxAmount) * 100;

  const milestone = document.createElement('div');
  milestone.className = 'hdt-promo-bar__milestone';
  milestone.style.backgroundColor = milestoneColor;
  milestone.setAttribute('data-amount', this.formatPrice(level.amount));

  // POSICIÓN CORREGIDA
  if (percentPosition >= 98) {
    milestone.style.left = '100%';
    milestone.style.transform = 'translateX(-100%)'; // $3,500 entra completo
  } else {
    milestone.style.left = percentPosition + '%';
    milestone.style.transform = 'translateX(-50%)';
  }

  milestonesContainer.appendChild(milestone);
});

  }

  getPreviousLevel(currentAmount) {
    const allLevels = this.getDiscountLevels();
    let prevLevel = null;
    for (let level of allLevels) {
      if (level.amount < currentAmount) {
        prevLevel = level;
      }
    }
    return prevLevel;
  }

  formatPrice(amount) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PromoBar();
  });
} else {
  new PromoBar();
}


